import React, { Fragment } from 'react';
import {
  Typography,
  Paper,
  Grid,
  withStyles,
  createStyles,
  Card,
  CardContent,
  Divider,
  TextField,
  Button,
  ListItemText,
  ListItem,
  CircularProgress,
  Snackbar
} from '@material-ui/core';
import Header from './Header';
import { getBillSubmissionPaymentRequest } from '../utils/PaymentRequestApiUtils';
import moment from 'moment';
import PaymentSearchIcon from '../images/payment-search.png'
import { statusMap } from '../constants/paymentStatus';
import { withRouter } from 'react-router';
import Tag from './Tag';

const styles = (theme) => createStyles({
  root: {
    flexGrow: 1,
    backgroundColor: '#FFFFFF',
    height: 'calc(100vh - 16px)',
  },
  container: {
    padding: '1rem',
    [theme.breakpoints.down('sm')]: {
      padding: '0.7rem',
    },
  },
  inputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  textField: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  button: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginBottom: '1rem',
    },
  },
  timeline: {
    position: 'relative',
    padding: '20px 0',
    margin: '0 auto',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      bottom: 0,
      width: 2,
      background: '#ddd',
      left: '50%',
      marginLeft: -1,
    },
    [theme.breakpoints.down('sm')]: {
      '&::before': {
        left: '30px',
      },
    },
  },
  timelineItem: {
    padding: '10px 40px',
    position: 'relative',
    background: 'inherit',
    width: '50%',
    boxSizing: 'border-box',
    '&:nth-of-type(odd)': {
      left: 0,
    },
    '&:nth-of-type(even)': {
      left: '50%',
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      paddingLeft: '70px',
      paddingRight: '25px',
      '&:nth-of-type(odd), &:nth-of-type(even)': {
        left: 0,
      },
    },
  },
  timelineContent: {
    padding: '15px',
    background: 'white',
    position: 'relative',
    borderRadius: '6px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
  },
  timelineDot: {
    width: 16,
    height: 16,
    position: 'absolute',
    background: '#2196f3',
    borderRadius: '50%',
    top: '22px',
    zIndex: 1,
    [theme.breakpoints.down('sm')]: {
      left: '22px',
    },
  },
  timelineDotLeft: {
    right: -8,
    [theme.breakpoints.down('sm')]: {
      right: 'auto',
    },
  },
  timelineDotRight: {
    left: -8,
    [theme.breakpoints.down('sm')]: {
      left: '22px',
    },
  },
  date: {
    display: 'inline-block',
    padding: '5px 10px',
    background: '#2196f3',
    color: 'white',
    borderRadius: '4px',
    fontSize: '14px',
    marginBottom: '10px',
  },
  additionalDetails: {
    marginTop: '20px',
  },
  mainTitle: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.5rem',
    },
  },
  subtitle: {
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9rem',
    },
  },
});

class PaymentRequestTimeline extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      billSubmissionId: '',
      data: [],
      inProgress: false,
      snackOpen: false,
      snackMessage: '',
    };
  }


  componentDidMount() {
    const id = this.props.location && this.props.location.query.id;
    if (id && id.includes(',')) {
      this.setState({
        snackOpen: true,
        snackMessage: 'Please enter a single Bill Submission ID'
      })

      return;
    }
    if (id) {
      this.setState({
        billSubmissionId: id
      }, () => this.submitFilter());
    }
  }

  handleBillSubmissionIdChange = (event) => {
    const value = event.target.value
    if (value !== '' && value.includes(',')) {
      this.setState({
        snackOpen: true,
        snackMessage: 'Please enter a single Bill Submission ID'
      })

      return;
    }
    this.setState({ billSubmissionId: value });
  }

  submitFilter = () => {
    // Validate billSubmissionIds input
    if (!this.state.billSubmissionId) {
      this.setState({ snackOpen: true, snackMessage: 'Please enter Bill Submission IDs' });
      return;
    }

    // Add query parameter to URL without reloading the page
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: { id: this.state.billSubmissionId }
    });

    // Fetch data using the new API call
    this.handleFetchData({ bill_submission_ids: this.state.billSubmissionId });
  }

  handleFetchData = (filters = {}) => {
    this.setState({ inProgress: true }, () => {
      getBillSubmissionPaymentRequest(filters).then(response => {
        if (response) {
          if (response.status === 'success') {
            this.setState({ data: response.data, inProgress: false })
          }
          else {
            this.setState({ snackMessage: response.message, snackOpen: true, inProgress: false })
          }
        }
      })
    })
  }


  handleCloseSnack = () => {
    this.setState({ snackOpen: false })
  }

  clearFilter = () => {
    this.props.router.push({
      pathname: this.props.location.pathname,
      query: {}
    });
    this.setState({
      billSubmissionId: '',
      data: []
    });
  }

  formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  getTimelineEvents = (payment = null) => {
    if (payment === null || payment === undefined) {
      return []
    }
    const events = []

    events.push({
      date: payment.bill_submissions_date,
      title: 'Bill Submitted',
      type: 'bill_submission_creation',
      description: `Submitted by ${payment.bs_requesterEmail}`,
    })

    if (payment.L1_approver_date) {
      events.push({
        date: payment.L1_approver_date,
        title: 'Bill-Sub L1 Approval',
        type: 'bill_submission_approval',
        description: 'Approved by L1',
      })
    }

    if (payment.L2_approver_date) {
      events.push({
        date: payment.L2_approver_date,
        type: 'bill_submission_approval',
        title: 'Bill-Sub L2 Approval',
        description: 'Approved by L2',
      })
    }

    if (payment.L3_approver_date) {
      events.push({
        date: payment.L3_approver_date,
        type: 'bill_submission_approval',
        title: 'Bill-Sub L3 Approval',
        description: 'Approved by L3',
      })
    }

    if (payment.L4_approver_date) {
      events.push({
        date: payment.L4_approver_date,
        type: 'bill_submission_approval',
        title: 'Bill-Sub L4 Approval',
        description: 'Approved by L4',
      })
    }

    if (payment.created_at) {
      events.push({
        date: payment.created_at,
        type: 'zaps_pr_creation',
        title: 'PR Created in ZAPS',
        description: payment.paymentHoldAtProcessed === 1 
                      ? (
                        <span>
                          Payment Request created with <Tag color='info'>Hold at Processed</Tag> status
                        </span>
                      )
                      : 'Payment Request successfully created in ZAPS',
      })
    }

    if (payment.approverId1_at) {
      events.push({
        date: payment.approverId1_at,
        type: 'zaps_pr_approval',
        title: 'Zaps L1 Approval',
        description: `Approved by ${payment.zaps_approver_l1_name || 'L1 Approver'}`,
      })
    }

    if (payment.approverId2_at) {
      events.push({
        date: payment.approverId2_at,
        type: 'zaps_pr_approval',
        title: 'Zaps L2 Approval',
        description: `Approved by ${payment.zaps_approver_l2_name || 'L2 Approver'}`,
      })
    }

    if (payment.accounting_status_date) {
      events.push({
        date: payment.accounting_status_date,
        type: 'zaps_pr_accounting',
        title: 'Payment Accounted',
        description: `PR was Accounted by ${payment.accounting_status_by || 'Unknown'}`,
      })
    }

    if (payment.deleted_at) {
      events.push({
        date: payment.deleted_at,
        type: 'zaps_pr_deletion',
        title: 'Payment Deleted',
        description: `Deleted by ${payment.deleted_by || 'Unknown'}`,
      })
    } else if (payment.transaction_date) {
      events.push({
        date: payment.transaction_date,
        type: 'zaps_pr_completion',
        title: 'Transaction Completed',
        description: `Transaction ID: ${payment.transaction_id}`,
      })
    } else {
      events.push({
        date: new Date().toISOString(),
        title: 'Current Status',
        description: statusMap[payment.status].value,
        type: 'zaps_pr_currentStatus',
      })
    }

    const parseDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      // If the date is invalid, return null
      return isNaN(date.getTime()) ? null : date;
    };

    return events
      .map(event => ({
        ...event,
        parsedDate: parseDate(event.date),
        hasTime: event.date.includes('T')  // Check if the date string includes a time component
      }))
      .sort((a, b) => {
        if (a.parsedDate && b.parsedDate) {
          const aTime = a.parsedDate.getTime();
          const bTime = b.parsedDate.getTime();

          // If dates are the same, prioritize the one with time
          if (a.parsedDate.toDateString() === b.parsedDate.toDateString()) {
            if (a.hasTime && !b.hasTime) return -1;
            if (!a.hasTime && b.hasTime) return 1;
          }

          // Otherwise, sort by date
          return aTime - bTime;
        } else if (a.parsedDate) {
          return -1;
        } else if (b.parsedDate) {
          return 1;
        }
        return 0;
      });
  }

  handleCloseSnack = () => {
    this.setState({ snackOpen: false })
  }


  handleKeyDown = (event) => {
    if (event.key === 'Enter' && !this.state.inProgress) {
      this.submitFilter();
    }
  }

  render() {
    const { classes } = this.props;
    const { data, inProgress, snackOpen, snackMessage } = this.state;
    return (
      <div className={classes.root}>
        <Header />
        <div className={classes.container}>
          <div className={classes.inputContainer}>
            <div 
              style={{ 
                width: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                flexWrap: 'wrap' 
              }}
            >
              <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                Enter Bill Submission ID:&nbsp;&nbsp;&nbsp;&nbsp;
                <TextField
                  id="billSubmissionId"
                  type="text"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  value={this.state.billSubmissionId}
                  onChange={this.handleBillSubmissionIdChange}
                  onKeyDown={this.handleKeyDown}
                  className={classes.textField}
                  margin='normal'
                />
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  disabled={this.state.billSubmissionId === '' || inProgress}
                  onClick={this.submitFilter}
                  className={classes.button}
                >
                  Submit Filter
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  disabled={this.state.billSubmissionId === '' || inProgress}
                  onClick={this.clearFilter}
                  className={classes.button}
                  style={{ background: 'red' }}
                >
                  Clear Filter
                </Button>
              </div>
            </div>
          </div>
          {inProgress ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 'calc(100vh - 25rem)',
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            Array.isArray(data) && data.length > 0 && data[0] ? (
              <Fragment>
                <Paper style={{ padding: '1rem', marginTop: '10px' }}>
                  <Typography variant="h5" style={{ fontWeight: 'bold' }} gutterBottom>
                    Timeline
                  </Typography>
                  <Divider style={{ margin: '10px 0' }} />
                  <div className={classes.timeline}>
                    {this.getTimelineEvents(data[0]).map((item, index) => (
                      <div
                        key={index}
                        className={classes.timelineItem}
                      >
                        <div className={classes.timelineContent}>
                          <Typography variant="h6" component="h4" style={{ fontFamily: 'Inter, sans-serif' }}>{item.title}</Typography>
                          <Typography
                            color="textSecondary"
                            style={{ fontFamily: 'Inter, sans-serif' }}
                          >{this.formatDate(item.parsedDate)}</Typography>
                          <Typography style={{ fontFamily: 'Inter, sans-serif', marginTop: '10px' }}>{item.description}</Typography>
                        </div>
                        <div
                          className={`${classes.timelineDot} ${index % 2 === 0 ? classes.timelineDotLeft : classes.timelineDotRight
                            }`}
                        />
                      </div>
                    ))}
                  </div>
                </Paper>
                <Card className={classes.additionalDetails}>
                  <CardContent>
                    <Typography variant="h5" style={{ fontWeight: 'bold' }} gutterBottom>
                      Additional Details
                    </Typography>
                    <Divider style={{ margin: '10px 0' }} />
                    <Grid
                      container
                      spacing={2}
                      alignItems="center"
                      justifyContent="flex-start"
                    >
                      {data[0].payment_request_id ? (
                        <Grid item xs={12} sm={6} md={4}>
                          <ListItem>
                            <ListItemText style={{ width: '150px' }} primary="Payment Request ID" />
                            <ListItemText primary=":" />
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }} primary={data[0].payment_request_id} />
                          </ListItem>
                        </Grid>
                      ) : null}
                      {data[0].name ? (
                        <Grid item xs={12} sm={6} md={4}>
                          <ListItem>
                            <ListItemText style={{ width: '150px' }} primary="Vendor Name" />
                            <ListItemText primary=":" />
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }} primary={data[0].name} />
                          </ListItem>
                        </Grid>
                      ) : null}
                      {data[0].total_payable_amount ? (
                        <Grid item xs={12} sm={6} md={4}>
                          <ListItem>
                            <ListItemText style={{ width: '150px' }} primary="Total Payable Amount" />
                            <ListItemText primary=":" />
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }} primary={data[0].total_payable_amount} />
                          </ListItem>
                        </Grid>
                      ) : null}
                      {data[0].invoice_number && data[0].invoice_soft_copy_link ? (
                        <Grid item xs={12} sm={6} md={4}>
                          <ListItem>
                            <ListItemText style={{ width: '150px' }} primary="Invoice Number" />
                            <ListItemText primary=":" />
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }}>
                              <a href={data[0].invoice_soft_copy_link} target="_blank" rel="noopener noreferrer">{data[0].invoice_number}</a>
                            </ListItemText>
                          </ListItem>
                        </Grid>
                      ) : null}
                      {/* Supporting documents: JSON array (new-zaps) or plain URL (legacy) */}
                      {data[0].supporting_document_link ? (() => {
                        try {
                          const docs = JSON.parse(data[0].supporting_document_link);
                          if (Array.isArray(docs) && docs.length > 0) {
                            return (
                              <Grid item xs={12} sm={6} md={4}>
                                <ListItem>
                                  <ListItemText style={{ width: '150px' }} primary="Supporting Documents" />
                                  <ListItemText primary=":" />
                                </ListItem>
                                {docs.map((link, i) => (
                                  <ListItemText key={i} style={{ width: '115px', wordWrap: 'break-word' }}>
                                    <a href={link} target="_blank" rel="noopener noreferrer">
                                      Supporting Document {i + 1}
                                    </a>
                                  </ListItemText>
                                ))}
                              </Grid>
                            );
                          }
                        } catch (e) {}
                        // Plain URL fallback
                        return (
                          <Grid item xs={12} sm={6} md={4}>
                            <ListItem>
                              <ListItemText style={{ width: '150px' }} primary="Supporting Document" />
                              <ListItemText primary=":" />
                            </ListItem>
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }}>
                              <a href={data[0].supporting_document_link} target="_blank" rel="noopener noreferrer">{data[0].supporting_document_link}</a>
                            </ListItemText>
                          </Grid>
                        );
                      })() : null}
                      {data[0].invoice_due_date ? (
                        <Grid item xs={12} sm={6} md={4}>
                          <ListItem>
                            <ListItemText style={{ width: '150px' }} primary="Invoice Date" />
                            <ListItemText primary=":" />
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }} primary={moment(data[0].invoice_due_date).format('DD-MM-YYYY')} />
                          </ListItem>
                        </Grid>
                      ) : null}
                      {data[0].accounting_status_date ? (
                        <Grid item xs={12} sm={6} md={4}>
                          <ListItem>
                            <ListItemText style={{ width: '150px' }} primary="Accounting Status" />
                            <ListItemText primary=":" />
                            <ListItemText style={{ width: '115px', wordWrap: 'break-word' }} primary={(
                              <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                                PR was Accounted on {moment(data[0].accounting_status_date).format('DD-MM-YYYY hh:mm A')} {data[0].accounting_status_by && 'by ' + data[0].accounting_status_by}
                              </span>
                            )} />
                          </ListItem>
                        </Grid>
                      ) : null}
                    </Grid>
                  </CardContent>
                </Card>
              </Fragment>
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  height: 'calc(100vh - 25rem)',
                  width: '100%'
                }}
              >
                <img
                  src={PaymentSearchIcon}
                  alt='search-icon'
                  height={150}
                  width={150}
                />
                <p 
                  style={{
                    fontSize: '14px',
                    lineHeight: '25px',
                    fontWeight: '500',
                    color: '#171A1FFF',
                    marginTop: '10px'
                  }}
                >
                  Waiting to search...
                </p>
              </div>
            )
          )}
        </div>
        <Snackbar
          open={snackOpen}
          message={snackMessage}
          autoHideDuration={2500}
          onClose={this.handleCloseSnack}
        />
      </div>
    );
  }
}

export default withRouter(withStyles(styles)(PaymentRequestTimeline));



// WEBPACK FOOTER //
// ./src/components/PaymentRequestTimeline.js